/**
 * CompetitionDashboard.jsx (iter226) — The judge's home screen inside the
 * demo tenant. Shows live warehouse situation numbers, the 3-objective
 * challenge tracker, scenario cards, WHY STOCKARIS explainer, and a
 * highly visible reset button.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Package, Warehouse, Warning, Truck, MagnifyingGlass, Stack, WifiHigh,
  ArrowClockwise, ArrowRight, CheckCircle, Lightbulb, Trophy, Buildings,
  ChartLineUp, X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  getCompetitionState, resetCompetitionDemo,
  completeCompetitionObjective, trackCompetitionEvent,
  runCompetitionWhatIf, getCompetitionPalletStatement,
  reconcileCompetitionPallet,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';

const SITUATION_TILES = [
  { key: 'inbound_containers', label: 'Inbound', unit: 'containers arriving', icon: Truck, tone: 'sky' },
  { key: 'expected_pallets', label: 'Expected pallets', unit: '', icon: Package, tone: 'sky' },
  { key: 'available_locations', label: 'Available locations', unit: '', icon: Warehouse, tone: 'amber' },
  { key: 'inventory_alerts', label: 'Inventory alerts', unit: '', icon: Warning, tone: 'red' },
  { key: 'cycle_count_discrepancies', label: 'Cycle-count discrepancies', unit: '', icon: MagnifyingGlass, tone: 'red' },
  { key: 'urgent_orders', label: 'Urgent customer orders', unit: '', icon: Stack, tone: 'emerald' },
];

const SCENARIOS = [
  {
    key: 'investigate_discrepancy',
    title: 'Where did the missing stock go?',
    subtitle: 'Investigate a stock discrepancy',
    body: 'A cycle count has found a variance. Do not just accept the number. Trace it.',
    cta: 'Open Discrepancy Investigation',
    target: '/discrepancy-investigation',
    why: {
      problem: 'A traditional WMS shows the variance but leaves the investigation to Excel and Power BI.',
      approach: 'Stockaris links receiving, put-away, picks, adjustments, transfers, and operator activity into a single case timeline so you can see WHERE to look.',
      impact: 'Faster root cause. Fewer repeat errors. Higher trust in the number on screen.',
    },
    tone: 'emerald',
  },
  {
    key: 'assess_inbound_capacity',
    title: 'Can we fit the inbound?',
    subtitle: 'Four containers are arriving.',
    body: 'Do you have enough locations, or do you need to consolidate before Monday morning?',
    cta: 'Open Warehouse Layout',
    target: '/warehouse-layout',
    why: {
      problem: 'Most WMS discover the capacity problem when the truck is at the dock.',
      approach: 'Stockaris projects the inbound against current occupancy and highlights consolidation candidates BEFORE the container arrives.',
      impact: 'No dock surprises. No pallets stranded in the yard.',
    },
    tone: 'sky',
  },
  {
    key: 'process_urgent_order',
    title: 'Pick an urgent order',
    subtitle: 'Experience Stockaris from the warehouse floor.',
    body: 'The office sees the order. The warehouse team drives the pick. Feel the difference.',
    cta: 'Open Orders (guided tour)',
    target: '/orders?scenario=picking_flow',
    why: {
      problem: 'Traditional WMS keep the picking decisions in the office.',
      approach: 'Stockaris puts scan, allocation, path, and confirmation on the floor with the person moving the stock.',
      impact: 'Fewer picking errors. Faster dispatch. Less waiting on admin.',
    },
    tone: 'amber',
  },
];

// iter227 — Bonus scenarios shown below the core 3.
const BONUS_SCENARIOS = [
  {
    key: 'master_planner_whatif',
    title: 'Master Planner What-If',
    subtitle: 'Major Retailer walks in with +35%.',
    body: 'Run the surge against current stock and see the projected shortage + PO suggestion before you say yes.',
    cta: 'Run the What-If',
    kind: 'whatif',
    why: {
      problem: 'Sales says yes. The warehouse pays the price.',
      approach: 'Stockaris projects the additional demand against on-hand stock, ranks the hot SKUs, and suggests a purchase order with a 20% safety buffer.',
      impact: 'Test the warehouse impact before you commit to the customer.',
    },
    tone: 'sky',
  },
  {
    key: 'abc_layout',
    title: 'Is your layout efficient?',
    subtitle: 'ABC velocity overlay on the Sydney DC.',
    body: 'See A/B/C tiles based on the seeded 90-day movement data. Look for hot SKUs in cold corners.',
    cta: 'Open Layout Designer (ABC on)',
    target: '/warehouse-layout?abc=1&scenario=abc_layout',
    kind: 'link',
    why: {
      problem: "A warehouse shouldn't be rearranged because a diagram looks prettier.",
      approach: 'Stockaris overlays real movement data onto the layout and lets you simulate pick paths before you move a pallet.',
      impact: 'Measurable pick-time savings. Fewer congestion points. Evidence-based decisions.',
    },
    tone: 'emerald',
  },
  {
    key: 'picking_flow_guided',
    title: 'Guided pick + pack walkthrough',
    subtitle: 'Same seeded order — bring the tour with you.',
    body: 'Open Orders with a persistent tour panel that walks a judge through the whole warehouse-floor flow.',
    cta: 'Start guided pick',
    target: '/orders?scenario=picking_flow',
    kind: 'link',
    why: {
      problem: 'Most demos are read-only. Judges can look but never touch the workflow.',
      approach: 'Stockaris runs the real screens with a bottom-right tour explaining each step as the judge clicks through.',
      impact: 'Five minutes of muscle-memory instead of a slideshow.',
    },
    tone: 'amber',
  },
  {
    key: 'warehouse_transfer',
    title: "Don't let paperwork decide when stock becomes available.",
    subtitle: 'Warehouse transfer · Paperwork vs the floor',
    body: 'A hero inbound transfer is waiting. RF check-off drives when stock becomes available, not the office admin.',
    cta: 'Open Transfers (guided)',
    target: '/transfers?scenario=warehouse_transfer',
    kind: 'link',
    why: {
      problem: 'Most WMS mark inbound transfers as "received" the moment paperwork lands. The warehouse then waits — and picks fail.',
      approach: 'Stockaris ties stock availability to the physical RF check-off on the dock. The warehouse team drives the state change.',
      impact: 'No ghost stock. No pickers hunting for pallets that "should" be here.',
    },
    tone: 'sky',
  },
  {
    key: 'pallet_tracking',
    title: 'Where are your pallets going?',
    subtitle: 'CHEP / Loscam · Statement variance drill-down',
    body: 'The pooler statement says +60 pallets more than the ledger. Open Pallet Management and hunt the variance.',
    cta: 'Open Pallet Ledger (guided)',
    target: '/pallets?scenario=pallet_tracking',
    kind: 'palletpanel',
    why: {
      problem: 'Pallet balances live in another spreadsheet. Every month a warehouse manager burns hours reconciling.',
      approach: 'Stockaris turns pallet tracking into an operational process — every movement is booked, every variance drillable.',
      impact: 'Fewer pooler charges. Faster reconciliations. No more spreadsheet Friday.',
    },
    tone: 'emerald',
  },
  {
    key: 'offline_simulation',
    title: 'What happens when the Wi-Fi disappears?',
    subtitle: 'Simulate internet failure',
    body: 'Drop the connection, keep working. Watch the queue drain when the network returns.',
    cta: 'Simulate Internet Failure',
    target: '/competition-demo/offline?scenario=offline_simulation',
    kind: 'link',
    why: {
      problem: 'A dead Wi-Fi router stops most WMS dead. Pickers, receivers and counters all wait for the network to come back.',
      approach: 'Stockaris keeps the warehouse floor productive during outages — work queues locally, then syncs the moment the network returns.',
      impact: 'No lost hours. No paper-based fallback shuffle. No missed dispatch cutoffs.',
    },
    tone: 'red',
  },
];

export default function CompetitionDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [openWhy, setOpenWhy] = useState(null);
  const [whatIfOpen, setWhatIfOpen] = useState(false);
  const [palletOpen, setPalletOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await getCompetitionState();
      setState(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load demo state');
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    trackCompetitionEvent('demo.dashboard_viewed').catch(() => {});
  }, []);
  useEffect(() => {
    if (state?.challenge_complete) {
      // Celebratory pause so the judge sees the transition rather than a
      // hard route swap.
      toast.success('Challenge complete — all 3 objectives cleared.', {
        duration: 3000,
        description: 'Taking you to the end screen…',
      });
      const t = setTimeout(() => navigate('/competition-demo/complete'), 3000);
      return () => clearTimeout(t);
    }
  }, [state?.challenge_complete, navigate]);

  const doReset = async () => {
    if (!window.confirm('Reset the Competition Demo to its original state?')) return;
    setResetting(true);
    trackCompetitionEvent('demo.reset_used').catch(() => {});
    try {
      await resetCompetitionDemo();
      await load();
      toast.success('Demo reset — every scenario is fresh again.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const openScenario = (sc) => {
    trackCompetitionEvent('demo.scenario_opened', sc.key).catch(() => {});
    completeCompetitionObjective(sc.key).catch(() => {});
    navigate(sc.target);
  };

  if (!state) {
    return <Layout><div className="p-6 text-sm text-muted-foreground">Loading challenge…</div></Layout>;
  }

  const s = state.situation || {};
  const done = state.objectives_done_count || 0;
  const total = state.objectives_total || 3;

  return (
    <Layout>
      <div className="space-y-6 p-1" data-testid="competition-dashboard">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-emerald-600 mb-1">
              <Trophy size={14} weight="duotone" /> Competition Demo · {state.company_name}
            </div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight">Your Warehouse Challenge</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Signed in as {user?.email}. Every action here is scoped to an isolated demo tenant.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="rounded-none bg-emerald-500 text-neutral-950 uppercase font-black tracking-widest text-[10px]">
              {done}/{total} Complete
            </Badge>
            <Button variant="outline" onClick={doReset} disabled={resetting}
              className="rounded-none border-red-500 text-red-600 hover:bg-red-50 uppercase tracking-widest text-xs font-black"
              data-testid="cd-reset-btn">
              <ArrowClockwise size={14} className="mr-1" />
              {resetting ? 'Resetting…' : 'Reset Demo'}
            </Button>
          </div>
        </div>

        {/* Current Warehouse Situation */}
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground mb-2">
            Current Warehouse Situation
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-2">
            {SITUATION_TILES.map((t) => {
              const Icon = t.icon;
              const value = s[t.key];
              return (
                <Card key={t.key} className="p-4 rounded-none border-l-4 border-neutral-300">
                  <Icon size={20} weight="duotone" className={`mb-2 text-${t.tone}-500`} />
                  <div className="text-3xl font-black tabular-nums leading-none" data-testid={`cd-sit-${t.key}`}>
                    {value ?? '—'}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                    {t.label}{t.unit ? ` · ${t.unit}` : ''}
                  </div>
                </Card>
              );
            })}
            <Card className="p-4 rounded-none border-l-4 border-orange-500">
              <Warehouse size={20} weight="duotone" className="mb-2 text-orange-500" />
              <div className="text-3xl font-black leading-none" data-testid="cd-sit-warehouse_capacity">{s.warehouse_capacity || 'Moderate'}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Warehouse capacity</div>
            </Card>
            <Card className="p-4 rounded-none border-l-4 border-emerald-500">
              <WifiHigh size={20} weight="duotone" className="mb-2 text-emerald-500" />
              <div className="text-3xl font-black leading-none" data-testid="cd-sit-internet_online">{s.internet_online ? 'Online' : 'Offline'}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Internet connection</div>
            </Card>
          </div>
        </div>

        {/* Objectives tracker */}
        <Card className="p-6 rounded-none bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-500" data-testid="cd-objectives-card">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-emerald-700 mb-4">
            <Trophy size={14} weight="duotone" /> The Stockaris Warehouse Challenge · {done}/{total} complete
          </div>
          <div className="text-sm text-neutral-700 mb-4 max-w-3xl">
            You are the warehouse manager. Four containers are arriving. More than 100 pallets are expected. Only a handful of locations appear available. A cycle count has discovered a significant inventory discrepancy. An urgent customer order is waiting. Your job is to determine what is happening and decide what to do next.
          </div>
          <ol className="space-y-2">
            {state.objectives.map((o, i) => (
              <li key={o.key}
                className={`flex items-center gap-3 p-3 border-l-4 ${o.done ? 'border-emerald-500 bg-emerald-50' : 'border-neutral-300 bg-white'}`}
                data-testid={`cd-obj-${o.key}`}>
                {o.done ? <CheckCircle size={22} weight="fill" className="text-emerald-500" /> : <span className="w-6 h-6 rounded-full bg-neutral-200 text-neutral-700 text-xs font-black flex items-center justify-center">{i + 1}</span>}
                <div className="flex-1">
                  <div className="text-sm font-bold">{o.title}</div>
                </div>
                {o.done && <span className="text-[10px] uppercase tracking-widest text-emerald-600 font-black">Done</span>}
              </li>
            ))}
          </ol>
        </Card>

        {/* Scenario cards */}
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground mb-2">
            Scenarios
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="cd-scenarios-grid">
            {SCENARIOS.map((sc) => (
              <ScenarioCard key={sc.key} sc={sc}
                done={state.objectives.find((o) => o.key === sc.key)?.done}
                openWhy={openWhy} setOpenWhy={setOpenWhy}
                onOpen={() => openScenario(sc)} />
            ))}
          </div>
        </div>

        {/* Bonus scenarios (iter227) */}
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground mb-2">
            Bonus scenarios · Level 2
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="cd-bonus-scenarios-grid">
            {BONUS_SCENARIOS.map((sc) => (
              <ScenarioCard key={sc.key} sc={sc}
                openWhy={openWhy} setOpenWhy={setOpenWhy}
                onOpen={() => {
                  trackCompetitionEvent('demo.scenario_opened', sc.key).catch(() => {});
                  if (sc.kind === 'whatif') {
                    setWhatIfOpen(true);
                  } else if (sc.kind === 'palletpanel') {
                    setPalletOpen(true);
                  } else {
                    navigate(sc.target);
                  }
                }} />
            ))}
          </div>
        </div>

        {whatIfOpen && <WhatIfPanel onClose={() => setWhatIfOpen(false)} />}
        {palletOpen && <PalletStatementPanel onClose={() => setPalletOpen(false)}
                                              onNavigate={() => navigate('/pallets?scenario=pallet_tracking')} />}

        {/* Explore the full product */}
        <Card className="p-4 rounded-none border-dashed border-2 border-neutral-300 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-neutral-500">Explore freely</div>
            <div className="text-sm">Wander any Stockaris screen from the sidebar. Nothing you do here can escape the demo tenant.</div>
          </div>
          <Button onClick={() => navigate('/')} variant="outline" className="rounded-none uppercase tracking-widest text-xs" data-testid="cd-open-main-btn">
            <Buildings size={14} className="mr-2" /> Open Stockaris dashboard
          </Button>
        </Card>
      </div>
    </Layout>
  );
}


// ---------------------------------------------------------------------------
// Small helpers rendered inside the dashboard
// ---------------------------------------------------------------------------
function ScenarioCard({ sc, done, openWhy, setOpenWhy, onOpen }) {
  return (
    <Card className={`rounded-none border-l-4 p-5 flex flex-col border-${sc.tone}-500 hover:shadow-lg transition`}>
      <div className={`text-[10px] uppercase tracking-widest font-black text-${sc.tone}-600 mb-1`}>{sc.subtitle}</div>
      <h3 className="text-lg font-black leading-tight">{sc.title}</h3>
      <p className="text-xs text-muted-foreground mt-2 flex-1">{sc.body}</p>

      <div className="flex items-center gap-2 mt-4">
        <Button onClick={onOpen}
          className={`rounded-none text-xs font-black uppercase tracking-widest bg-${sc.tone}-500 hover:bg-${sc.tone}-600`}
          data-testid={`cd-scenario-open-${sc.key}`}>
          {done ? 'Revisit' : sc.cta} <ArrowRight size={12} className="ml-1" />
        </Button>
        <Button variant="ghost" size="sm"
          onClick={() => setOpenWhy(openWhy === sc.key ? null : sc.key)}
          className="text-xs uppercase tracking-widest text-muted-foreground"
          data-testid={`cd-scenario-why-${sc.key}`}>
          <Lightbulb size={12} className="mr-1" /> Why Stockaris?
        </Button>
      </div>

      {openWhy === sc.key && (
        <div className="mt-4 space-y-2 text-xs border-t border-neutral-200 pt-3" data-testid={`cd-why-body-${sc.key}`}>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-red-600">The problem</div>
            <p className="text-neutral-700">{sc.why.problem}</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-emerald-600">The Stockaris approach</div>
            <p className="text-neutral-700">{sc.why.approach}</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-sky-600">The business impact</div>
            <p className="text-neutral-700">{sc.why.impact}</p>
          </div>
        </div>
      )}
    </Card>
  );
}

function WhatIfPanel({ onClose }) {
  const [surge, setSurge] = useState(35);
  const [topN, setTopN] = useState(10);
  const [customer, setCustomer] = useState('Major Retailer');
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    setBusy(true);
    trackCompetitionEvent('demo.whatif_run', 'master_planner_whatif', { surge, topN }).catch(() => {});
    try {
      const r = await runCompetitionWhatIf({ surge_pct: surge, top_n: topN, customer_name: customer });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'What-If failed');
    } finally { setBusy(false); }
  };

  React.useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const t = data?.totals || {};

  return (
    <Card className="rounded-none border-l-4 border-sky-500 p-5 relative" data-testid="cd-whatif-panel">
      <button onClick={onClose} className="absolute top-3 right-3 text-neutral-400 hover:text-neutral-800" data-testid="cd-whatif-close">
        <X size={16} />
      </button>
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-sky-700 mb-2">
        <ChartLineUp size={14} weight="duotone" /> Master Planner What-If
      </div>
      <div className="flex gap-3 items-end flex-wrap mb-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block">Customer</label>
          <input value={customer} onChange={(e) => setCustomer(e.target.value)}
            className="border border-neutral-300 px-2 py-1 rounded-none text-sm w-56"
            data-testid="cd-whatif-customer" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block">Surge %</label>
          <input type="number" min="1" max="500" value={surge}
            onChange={(e) => setSurge(parseInt(e.target.value || '0'))}
            className="border border-neutral-300 px-2 py-1 rounded-none text-sm w-24 tabular-nums"
            data-testid="cd-whatif-surge" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block">Top N SKUs</label>
          <input type="number" min="1" max="50" value={topN}
            onChange={(e) => setTopN(parseInt(e.target.value || '0'))}
            className="border border-neutral-300 px-2 py-1 rounded-none text-sm w-20 tabular-nums"
            data-testid="cd-whatif-topn" />
        </div>
        <Button size="sm" onClick={run} disabled={busy}
          className="rounded-none bg-sky-500 hover:bg-sky-600 uppercase tracking-widest text-xs font-black"
          data-testid="cd-whatif-run">
          {busy ? 'Running…' : 'Run What-If'}
        </Button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
            <Card className="p-3 rounded-none">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Current qty</div>
              <div className="text-xl font-black tabular-nums">{t.current_qty}</div>
            </Card>
            <Card className="p-3 rounded-none">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Projected demand</div>
              <div className="text-xl font-black tabular-nums">{t.projected_demand}</div>
            </Card>
            <Card className="p-3 rounded-none border-l-4 border-red-500" data-testid="cd-whatif-shortfall">
              <div className="text-[10px] uppercase tracking-widest text-red-600">Shortfall</div>
              <div className="text-xl font-black tabular-nums text-red-600">{t.shortfall}</div>
            </Card>
            <Card className="p-3 rounded-none border-l-4 border-emerald-500">
              <div className="text-[10px] uppercase tracking-widest text-emerald-600">Suggested PO</div>
              <div className="text-xl font-black tabular-nums">{t.suggested_po_qty}</div>
              <div className="text-[10px] text-muted-foreground">${(t.suggested_po_cost || 0).toLocaleString()}</div>
            </Card>
            <Card className="p-3 rounded-none">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Extra pallets</div>
              <div className="text-xl font-black tabular-nums">{data.capacity_impact?.extra_pallets || 0}</div>
            </Card>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="cd-whatif-table">
              <thead>
                <tr className="text-left border-b text-muted-foreground">
                  <th className="py-1 pr-2">SKU</th>
                  <th className="py-1 pr-2">Name</th>
                  <th className="py-1 pr-2 text-right">Current</th>
                  <th className="py-1 pr-2 text-right">Baseline</th>
                  <th className="py-1 pr-2 text-right">Projected</th>
                  <th className="py-1 pr-2 text-right">Shortfall</th>
                  <th className="py-1 pr-2 text-right">Suggest PO</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.sku_code} className="border-b last:border-0">
                    <td className="py-1 pr-2 font-mono">{r.sku_code}</td>
                    <td className="py-1 pr-2">{r.name}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{r.current_qty}</td>
                    <td className="py-1 pr-2 text-right tabular-nums text-muted-foreground">{r.baseline_demand}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{r.projected_demand}</td>
                    <td className={`py-1 pr-2 text-right tabular-nums font-bold ${r.shortfall > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {r.shortfall}
                    </td>
                    <td className="py-1 pr-2 text-right tabular-nums">{r.suggested_po_qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-3">
            Stockaris identifies the warehouse impact before you commit to the customer.
          </div>
        </>
      )}
    </Card>
  );
}

function PalletStatementPanel({ onClose, onNavigate }) {
  const navigate = useNavigate();
  const [pool, setPool] = useState('CHEP');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [lastReconcile, setLastReconcile] = useState(null);   // {reference, qty}

  const load = async (code) => {
    setBusy(true);
    try {
      const r = await getCompetitionPalletStatement(code);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Statement fetch failed');
    } finally { setBusy(false); }
  };
  React.useEffect(() => { load(pool); /* eslint-disable-next-line */ }, [pool]);

  const reconcile = async () => {
    if (!data || data.variance === 0) return;
    setReconciling(true);
    trackCompetitionEvent('demo.pallet_reconciled', 'pallet_tracking', { pool, variance: data.variance }).catch(() => {});
    try {
      const r = await reconcileCompetitionPallet(pool);
      toast.success(r.data?.message || 'Ledger reconciled');
      setLastReconcile({ reference: r.data?.reference, qty: r.data?.adjustment_qty });
      await load(pool);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reconcile failed');
    } finally { setReconciling(false); }
  };

  return (
    <Card className="rounded-none border-l-4 border-emerald-500 p-5 relative" data-testid="cd-pallet-panel">
      <button onClick={onClose} className="absolute top-3 right-3 text-neutral-400 hover:text-neutral-800" data-testid="cd-pallet-close">
        <X size={16} />
      </button>
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-emerald-700 mb-2">
        <Stack size={14} weight="duotone" /> Pallet Statement · {pool}
      </div>
      <div className="flex gap-1 mb-3" data-testid="cd-pallet-pool-tabs">
        {['CHEP', 'LOSCAM', 'PLAIN'].map((c) => (
          <Button key={c} size="sm" variant={pool === c ? 'default' : 'outline'}
            className={`rounded-none uppercase tracking-widest text-xs ${pool === c ? 'bg-emerald-500' : ''}`}
            onClick={() => setPool(c)}
            data-testid={`cd-pallet-pool-${c}`}>
            {c}
          </Button>
        ))}
      </div>

      {busy && <div className="text-xs text-muted-foreground">Loading statement…</div>}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
            <Card className="p-3 rounded-none">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Opening</div>
              <div className="text-xl font-black tabular-nums">{data.opening_balance}</div>
            </Card>
            <Card className="p-3 rounded-none">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Receipts</div>
              <div className="text-xl font-black tabular-nums text-emerald-600">+{data.receipts}</div>
            </Card>
            <Card className="p-3 rounded-none">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Dispatches</div>
              <div className="text-xl font-black tabular-nums text-red-600">−{data.dispatches}</div>
            </Card>
            <Card className="p-3 rounded-none">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Adjustments</div>
              <div className="text-xl font-black tabular-nums">{data.adjustments}</div>
            </Card>
            <Card className="p-3 rounded-none border-l-4 border-emerald-500">
              <div className="text-[10px] uppercase tracking-widest text-emerald-600">Expected</div>
              <div className="text-xl font-black tabular-nums" data-testid="cd-pallet-expected">{data.expected_balance}</div>
            </Card>
            <Card className={`p-3 rounded-none border-l-4 ${data.variance === 0 ? 'border-emerald-500' : 'border-red-500'}`} data-testid="cd-pallet-variance-card">
              <div className={`text-[10px] uppercase tracking-widest ${data.variance === 0 ? 'text-emerald-600' : 'text-red-600'}`}>Variance</div>
              <div className={`text-xl font-black tabular-nums ${data.variance === 0 ? '' : 'text-red-600'}`}
                    data-testid="cd-pallet-variance">
                {data.variance > 0 ? '+' : ''}{data.variance}
              </div>
              <div className="text-[10px] text-muted-foreground">vs statement {data.statement_balance}</div>
            </Card>
          </div>

          <div className="text-[11px] font-black uppercase tracking-widest text-neutral-500 mb-1">
            Top drill-down candidates (last 14 days)
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="cd-pallet-drill-table">
              <thead>
                <tr className="text-left border-b text-muted-foreground">
                  <th className="py-1 pr-2">Kind</th>
                  <th className="py-1 pr-2 text-right">Qty</th>
                  <th className="py-1 pr-2">Reference</th>
                  <th className="py-1 pr-2">When</th>
                </tr>
              </thead>
              <tbody>
                {data.drill_candidates.map((r) => (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="py-1 pr-2 font-mono uppercase">{r.kind}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{r.qty}</td>
                    <td className="py-1 pr-2 font-mono">{r.reference || '—'}</td>
                    <td className="py-1 pr-2 text-muted-foreground">{new Date(r.timestamp).toLocaleString()}</td>
                  </tr>
                ))}
                {data.drill_candidates.length === 0 && (
                  <tr><td colSpan={4} className="py-3 text-center text-xs italic text-muted-foreground">No large outbound movements in the window.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Stockaris turns pallet tracking into an operational process — not another spreadsheet.
            </div>
            <div className="flex items-center gap-2">
              {data.variance !== 0 && (
                <Button size="sm" onClick={reconcile} disabled={reconciling}
                  className="rounded-none bg-red-500 hover:bg-red-600 text-white text-xs font-black uppercase tracking-widest"
                  data-testid="cd-pallet-reconcile-btn">
                  {reconciling ? 'Booking…' : 'Fix variance in one tap'}
                </Button>
              )}
              {data.variance === 0 && (
                <div className="text-[10px] font-black uppercase tracking-widest text-emerald-600 flex items-center gap-2"
                     data-testid="cd-pallet-reconciled">
                  ✓ Ledger matches statement
                  {lastReconcile?.reference && (
                    <button
                      onClick={() => {
                        trackCompetitionEvent('demo.reconcile_navigate_to_pallets', 'pallet_tracking',
                          { reference: lastReconcile.reference }).catch(() => {});
                        navigate(`/pallets?scenario=pallet_tracking&highlight=${encodeURIComponent(lastReconcile.reference)}`);
                      }}
                      className="text-sky-600 underline hover:text-sky-800 normal-case tracking-normal font-bold"
                      data-testid="cd-pallet-see-landed"
                    >
                      See how it landed on the pooler statement page →
                    </button>
                  )}
                </div>
              )}
              <Button size="sm" onClick={onNavigate}
                className="rounded-none bg-emerald-500 hover:bg-emerald-600 text-xs font-black uppercase tracking-widest"
                data-testid="cd-pallet-open-ledger">
                Open Pallet Ledger <ArrowRight size={12} className="ml-1" />
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

